import { Dictionary } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { components } from 'react-select';
import styled from 'styled-components';
import { Role, Team } from 'api/models';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { Popover } from 'components/atoms/popover';
import { Option, OptionProps, Select } from 'components/atoms/select';
import {
  SingleLineLabel,
  VirtualizedMenuList,
} from 'components/atoms/select/custom-components';
import {
  DropdownIndicator,
  ListItem,
  ListItemMeta,
} from 'components/molecules/assignee-picker/select';
import {
  PlaceholderResolutionV2,
  PlaceholderResolutionsByProject,
  TeamInfoWithTaskIds,
} from 'queries/placeholder';
import { SelectContainer } from './timesheet-work-options';
import { TimesheetTableTimeEntry } from './types';

export const getDefaultRoleAndTeamForTimesheetEntry = (
  projectId: number,
  taskId: number | undefined,
  userId: number,
  defaultRoleId?: number,
  placeholderResolutions?: PlaceholderResolutionsByProject,
) => {
  let roleId;
  let teamId;

  if (placeholderResolutions != null) {
    const projectWisePlaceholders: PlaceholderResolutionV2 | undefined =
      placeholderResolutions[projectId];

    if (projectWisePlaceholders != null) {
      const userAssignedPlaceholder: TeamInfoWithTaskIds[] | undefined =
        projectWisePlaceholders.resolvedWithTaskIds[`user_${userId}`];

      if (userAssignedPlaceholder != null) {
        let replacedTeamForTask: TeamInfoWithTaskIds | undefined;

        if (taskId != null) {
          replacedTeamForTask = userAssignedPlaceholder.find((x) =>
            x.taskIds?.includes(taskId),
          );
        }
        if (replacedTeamForTask && replacedTeamForTask?.teamId != null) {
          roleId = replacedTeamForTask.roleId;
          teamId = replacedTeamForTask.teamId;
        } else {
          roleId = userAssignedPlaceholder[0].roleId;
          teamId = userAssignedPlaceholder[0].teamId;
        }
      }
    }
  }

  if (roleId == null && defaultRoleId != null) {
    roleId = defaultRoleId;
  }

  return { roleId, teamId };
};

export const getRoleTeamOptionsForTimeEntry = (
  userId?: number,
  projectId?: number,
  placeholderResolutions?: PlaceholderResolutionsByProject,
  roles?: Dictionary<Role>,
  teams?: Dictionary<Team>,
  defaultRoleId?: number,
) => {
  let optionsFinal: Option[] = [];

  if (placeholderResolutions != null && projectId != null && userId != null) {
    const projectWisePlaceholders: PlaceholderResolutionV2 | undefined =
      placeholderResolutions[projectId];

    if (projectWisePlaceholders != null) {
      const userAssignedPlaceholder: TeamInfoWithTaskIds[] | undefined =
        projectWisePlaceholders.resolvedWithTaskIds[`user_${userId}`];

      if (
        userAssignedPlaceholder != null &&
        userAssignedPlaceholder.length !== 0 &&
        defaultRoleId != null
      ) {
        const options: Option[] = [
          {
            value: `${defaultRoleId}`,
            label: roles?.[defaultRoleId]?.roleName ?? '',
            extra: { type: 'ROLE' },
          },
        ];

        optionsFinal = userAssignedPlaceholder.reduce((acc, x) => {
          if (x.roleId != null) {
            acc.push({
              value: `${x.roleId}_${x.teamId}`,
              label: roles?.[x.roleId]?.roleName ?? '',
              description: teams?.[x.teamId]?.teamName ?? '',
              extra: { type: 'TEAM' },
            });
          }

          return acc;
        }, options);
      }
    }
  }

  return { options: optionsFinal };
};

const selectStyles = {
  control: (provided) => ({
    ...provided,
    margin: 'var(--spacing-05)',
  }),
  menu: () => ({ boxShadow: 'none' }),
  option: (provided) => ({
    ...provided,
    paddingLeft: 'var(--spacing-05)',
    paddingRight: 'var(--spacing-05)',
  }),
};

const StyledTrigger = styled(Button)`
  && {
    height: 22px;
  }
`;
const StyledOption = styled(components.Option)`
  background-color: ${({ isSelected }) =>
    isSelected ? 'var(--primary-color)' : 'transparent'};
`;

const EntityOption = (props: OptionProps): JSX.Element => {
  const { data, isFocused, selectProps } = props;

  const isSelected = data.value === selectProps.value;

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading,@typescript-eslint/ban-ts-comment
    // @ts-ignore
    <StyledOption {...props} isSelected={isSelected}>
      <ListItem>
        <ListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          title={<SingleLineLabel>{data.label}</SingleLineLabel>}
          description={data.description}
          avatar={<Avatar name={data.label} />}
        />
      </ListItem>
    </StyledOption>
  );
};

export const RoleRenderer = ({
  roleOptions,
  defaultRoleId,
  record,
  updateTimeEntry,
  attachTo,
}: {
  roleOptions: Option[];
  defaultRoleId?: string;
  record?: TimesheetTableTimeEntry;
  updateTimeEntry: (ids: string) => void;
  attachTo?: HTMLElement | null;
}) => {
  const [visible, setVisible] = useState(false);
  const [value, setValue] = useState<string | undefined>(defaultRoleId);
  const roleChosen = roleOptions.find((x) => x.value === value);

  roleOptions.sort((a, b) => (a.label > b.label ? 1 : -1));

  const handleVisibleChange = (visibility: boolean) => {
    if (record?.minutes === 0) {
      return;
    }
    setVisible(visibility);
  };

  const onSelection = (props: Option) => {
    updateTimeEntry(String(props.value));
    setValue(String(props.value));
    setVisible(false);
  };

  useEffect(() => {
    let role: string | undefined;

    if (record?.roleId != null) {
      if (record?.teamId != null) {
        role = `${record?.roleId}_${record?.teamId}`;
      } else {
        role = `${record?.roleId}`;
      }
    }
    setValue(role ?? defaultRoleId);
  }, [defaultRoleId, record?.roleId, record?.teamId]);

  return (
    <Popover
      visible={visible}
      onVisibleChange={handleVisibleChange}
      content={
        <SelectContainer data-cy="timesheet-project-select">
          <Select
            menuIsOpen
            autoFocus
            components={{
              ClearIndicator: null,
              IndicatorSeparator: null,
              MenuList: VirtualizedMenuList,
              DropdownIndicator,
              Option: EntityOption,
            }}
            placeholder={<FormattedMessage id="placeholder.search" />}
            tabSelectsValue={false}
            styles={selectStyles}
            options={roleOptions}
            onChange={onSelection}
            noOptionsMessage={() => (
              <FormattedMessage id="settings.roles.list.empty.title" />
            )}
            value={value}
            type="user"
          />
        </SelectContainer>
      }
      placement="bottomLeft"
      trigger="click"
      isDisabled={record?.minutes === 0}
      getPopupContainer={attachTo ? () => attachTo as HTMLElement : undefined}
    >
      <StyledTrigger type="link" disabled={record?.minutes === 0}>
        {roleChosen?.label ? (
          roleChosen?.label
        ) : (
          <FormattedMessage id="choose" />
        )}
      </StyledTrigger>
    </Popover>
  );
};
